import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * Tiny Leaflet route map for a request card.
 * Renders two markers (pickup + delivery) and a red polyline between them.
 * Uses OpenStreetMap tiles — no API key.
 */
export default function RouteMap({ pickup, delivery, height = 140, className = "" }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!pickup || !delivery || pickup.lat == null || delivery.lat == null) return;

    if (!mapRef.current) {
      mapRef.current = L.map(containerRef.current, {
        zoomControl: false,
        attributionControl: false,
        dragging: false,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        tap: false,
        touchZoom: false,
      });
      // Dark tile layer
      L.tileLayer(
        "https://cartodb-basemaps-a.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png",
        { maxZoom: 19 }
      ).addTo(mapRef.current);
    }
    const map = mapRef.current;
    // clear old layers except tile
    map.eachLayer((layer) => {
      if (!(layer instanceof L.TileLayer)) map.removeLayer(layer);
    });
    const pickupLL = [pickup.lat, pickup.lng];
    const deliveryLL = [delivery.lat, delivery.lng];
    const pickupIcon = L.divIcon({
      className: "brv-marker brv-marker-pickup",
      iconSize: [14, 14],
      iconAnchor: [7, 7],
      html: '<div style="width:14px;height:14px;border:2px solid #FFFFFF;background:#FF3B30;border-radius:50%;box-shadow:0 0 0 2px rgba(255,59,48,0.35)"></div>',
    });
    const deliveryIcon = L.divIcon({
      className: "brv-marker brv-marker-delivery",
      iconSize: [14, 14],
      iconAnchor: [7, 7],
      html: '<div style="width:14px;height:14px;border:2px solid #FFFFFF;background:#22c55e;border-radius:50%;box-shadow:0 0 0 2px rgba(34,197,94,0.35)"></div>',
    });
    L.marker(pickupLL, { icon: pickupIcon }).addTo(map);
    L.marker(deliveryLL, { icon: deliveryIcon }).addTo(map);
    L.polyline([pickupLL, deliveryLL], {
      color: "#FF3B30",
      weight: 3,
      opacity: 0.85,
      dashArray: "6, 6",
    }).addTo(map);
    map.fitBounds(L.latLngBounds([pickupLL, deliveryLL]), { padding: [24, 24], maxZoom: 8 });
  }, [pickup, delivery]);

  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  if (!pickup || !delivery || pickup.lat == null || delivery.lat == null) return null;

  return (
    <div
      ref={containerRef}
      className={`bg-[#050505] border border-white/[0.06] ${className}`}
      style={{ height }}
      aria-hidden="true"
    />
  );
}
